import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CloudUpload, CreditCard, Info, Maximize2, Plus, Sparkles, Trash2, X } from 'lucide-react'
import { sileo } from 'sileo'
import LucaModal from '../../../../../ui/LucaModal'
import { Button } from '../../../../../ui/index'
import CreditCardStatementApi from '../../../../../../../api/creditCardStatement.api'
import { BankAPI } from '../../../../../../../api/bancos.api'
import CreditCardSelect from './CreditCardSelect'
import ModalCreateCreditCard from './ModalCreateCreditCard'
import robot from '../../../../../../../assets/robot.png'
import { useQueryGetOrganizationTokensAvailable } from '../../../../../../../queries/organizationQueries'
import { QueryKeys } from '../../../../../../../queries/querykeys'
import { CURRENCY_ID_BY_CODE, CLP_CURRENCY_ID, maskCardLabel, maskCardNumber } from './creditCard.utils'

const bankAPI = new BankAPI()

const normalizeText = value =>
  (value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')

const last4FromValue = value => {
  const digits = String(value || '').replace(/\D/g, '')
  return digits.slice(-4)
}

const isCardDiscrepancy = field => field === 'tarjeta' || field === 'banco' || field === 'moneda'

const cardCurrencyMatchesStatement = (card, statement) =>
  !statement?.monedaExtraida || !card?.currency || card.currency === statement.monedaExtraida

const extractedCardLabel = (statement, discrepancies = []) => {
  const last4 = last4FromValue(
    statement?.numeroTarjetaExtraido || discrepancies.find(item => item.field === 'tarjeta')?.extractedValue
  )
  const banco =
    statement?.bancoExtraido || discrepancies.find(item => item.field === 'banco')?.extractedValue || ''
  const currencySuffix = statement?.monedaExtraida ? ` (${statement.monedaExtraida})` : ''
  if (last4.length === 4 && banco) return `TC ${banco} ${maskCardNumber(last4)}${currencySuffix}`
  if (last4.length === 4) return `TC ${maskCardNumber(last4)}${currencySuffix}`
  return banco ? `${banco}${currencySuffix}` : '—'
}

const findLoadedCardMatch = (cards, statement, discrepancies = []) => {
  const extractedLast4 = last4FromValue(
    statement?.numeroTarjetaExtraido || discrepancies.find(item => item.field === 'tarjeta')?.extractedValue
  )
  const bankTarget = normalizeText(
    statement?.bancoExtraido || discrepancies.find(item => item.field === 'banco')?.extractedValue
  )

  if (extractedLast4.length !== 4) return null

  return (cards || []).find(card => {
    const last4Ok = normalizeText(card.last4Digits) === normalizeText(extractedLast4)
    if (!last4Ok) return false
    if (!cardCurrencyMatchesStatement(card, statement)) return false
    if (!bankTarget) return true
    const bankNorm = normalizeText(card.bankName)
    return bankNorm.includes(bankTarget) || bankTarget.includes(bankNorm)
  }) || null
}

const STATUS_POLL_INTERVAL_MS = 2000

const getCurrentPeriodoMes = () => {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

const MOVEMENT_COLUMNS = [
  { key: 'fecha', label: 'Fecha' },
  { key: 'lugarOperacion', label: 'Lugar' },
  { key: 'descripcion', label: 'Descripción' },
  { key: 'numeroCuota', label: 'N° Cuota' },
  { key: 'abono', label: 'Abono' },
  { key: 'cargo', label: 'Cargo' },
  { key: 'comentario', label: 'Comentarios' }
]

const EDITABLE_MOVEMENT_FIELDS = [
  { key: 'fecha', label: 'Fecha', type: 'text', placeholder: 'DD/MM/AAAA' },
  { key: 'descripcion', label: 'Descripción', type: 'text', placeholder: '' },
  { key: 'lugarOperacion', label: 'Lugar', type: 'text', placeholder: '' },
  { key: 'numeroCuota', label: 'N° Cuota', type: 'text', placeholder: '' },
  { key: 'valorCuotaMensual', label: 'Monto', type: 'number', placeholder: '0' },
  { key: 'comentario', label: 'Comentarios', type: 'text', placeholder: 'Agrega un comentario (opcional)' }
]

const MovementStatusBadge = ({
  isNew,
  similarExistente,
  movimientoParecidoFecha,
  movimientoParecidoDescripcion,
  movimientoParecidoMonto,
  isSimilarInfoOpen,
  onToggleSimilarInfo
}) => (
  <div className='flex flex-col items-start gap-1'>
    {similarExistente && isNew !== false ? (
      <div className='flex flex-col items-start gap-1'>
        <button
          type='button'
          onClick={onToggleSimilarInfo}
          title='Ver movimiento existente parecido'
          className='inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100'
        >
          Parecido a uno existente
          <Info size={11} />
        </button>
        {isSimilarInfoOpen && (
          <div className='w-56 rounded-lg border border-amber-200 bg-amber-50/80 px-2 py-1.5 text-[11px] text-amber-800'>
            <p className='font-semibold'>Movimiento existente parecido</p>
            <p>{movimientoParecidoFecha || '—'}</p>
            <p>{movimientoParecidoDescripcion || '—'}</p>
            <p>
              {typeof movimientoParecidoMonto === 'number'
                ? `$${movimientoParecidoMonto.toLocaleString('es-CL')}`
                : '—'}
            </p>
          </div>
        )}
      </div>
    ) : isNew === false ? (
      <span className='inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-500'>
        Ya existía
      </span>
    ) : (
      <span className='inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700'>
        Nuevo
      </span>
    )}
  </div>
)

const DISCREPANCY_LABELS = {
  tarjeta: 'Tarjeta',
  banco: 'Tarjeta',
  moneda: 'Tarjeta'
}

const getMonthDateRange = mes => {
  if (!mes) return { desde: '', hasta: '' }
  const [year, month] = mes.split('-').map(Number)
  const desde = `${mes}-01`
  const lastDay = new Date(year, month, 0).getDate()
  const hasta = `${mes}-${String(lastDay).padStart(2, '0')}`
  return { desde, hasta }
}

const ModalCreditCardStatement = ({ isOpen, onClose, businessId }) => {
  const queryClient = useQueryClient()
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedCard, setSelectedCard] = useState(null)
  const [periodoMes, setPeriodoMes] = useState(getCurrentPeriodoMes())
  const [error, setError] = useState(null)
  const [statementId, setStatementId] = useState(null)
  const [isMinimized, setIsMinimized] = useState(false)
  const [movimientosEdit, setMovimientosEdit] = useState([])
  const [resolvedDiscrepancyFields, setResolvedDiscrepancyFields] = useState([])
  const [discrepancyDismissedId, setDiscrepancyDismissedId] = useState(null)
  const [useAiByField, setUseAiByField] = useState({})
  const [showCreateCardModal, setShowCreateCardModal] = useState(false)
  const [openSimilarIndex, setOpenSimilarIndex] = useState(null)

  const { desde: periodoDesde, hasta: periodoHasta } = getMonthDateRange(periodoMes)

  const tokensInfoQuery = useQueryGetOrganizationTokensAvailable()

  const estimateTokensMutation = useMutation({
    mutationFn: file => CreditCardStatementApi.estimateTokens(file, businessId, selectedCard?.id)
  })

  const creditCardsQuery = useQuery({
    queryKey: ['credit-cards', businessId],
    queryFn: async () => {
      const response = await bankAPI.getCreditCards(businessId)
      return response.data
    },
    enabled: Boolean(businessId)
  })

  const statusQuery = useQuery({
    queryKey: ['credit-card-statement-status', businessId, statementId],
    queryFn: () => CreditCardStatementApi.getStatus(businessId, statementId),
    enabled: Boolean(businessId) && Boolean(statementId),
    refetchInterval: query => (query.state.data?.status === 'processing' ? STATUS_POLL_INTERVAL_MS : false),
    staleTime: STATUS_POLL_INTERVAL_MS,
    retry: (failureCount, statusError) =>
      statusError?.response?.status === 404 && failureCount < 8,
    retryDelay: 1500
  })

  const saveMutation = useMutation({
    mutationFn: () => CreditCardStatementApi.save(
      businessId,
      { bankId: selectedCard.bankId, creditCardId: selectedCard.id, periodoDesde, periodoHasta },
      selectedFile
    ),
    onSuccess: saved => {
      queryClient.setQueryData(['credit-card-statement-status', businessId, saved._id], saved)
      setStatementId(saved._id)
      setError(null)
    },
    onError: saveError => {
      const message = saveError.response?.data?.message || 'Error al guardar la cartola.'
      setError(message)
      setStatementId(null)
    }
  })

  const statement = statusQuery.data
  const isProcessing =
    saveMutation.isPending ||
    statement?.status === 'processing' ||
    (Boolean(statementId) && !statement && !statusQuery.isError)
  const isReviewing = statement?.status === 'needs_review'
  const isFinished = statement?.status === 'completed' || statement?.status === 'failed'

  const pendingDiscrepancies = useMemo(
    () => (statement?.discrepancies || [])
      .filter(item => isCardDiscrepancy(item.field) && !resolvedDiscrepancyFields.includes(item.field)),
    [statement, resolvedDiscrepancyFields]
  )

  const discrepancyGroups = useMemo(
    () => (pendingDiscrepancies.length
      ? [{ key: 'tarjeta', field: 'tarjeta', items: pendingDiscrepancies, type: 'card' }]
      : []),
    [pendingDiscrepancies]
  )

  const loadedCards = creditCardsQuery.data || []
  const loadedCardMatch = findLoadedCardMatch(loadedCards, statement, statement?.discrepancies || [])

  const isAiSelected = () => useAiByField.tarjeta ?? useAiByField.banco ?? Boolean(loadedCardMatch)

  const markDiscrepancyResolved = field => {
    setResolvedDiscrepancyFields(prev => (prev.includes(field) ? prev : [...prev, field]))
  }

  const acceptAiValue = () => {
    if (!loadedCardMatch) {
      setError('Debes crear primero esta tarjeta.')
      return false
    }

    setSelectedCard(loadedCardMatch)
    markDiscrepancyResolved('tarjeta')
    markDiscrepancyResolved('banco')
    markDiscrepancyResolved('moneda')
    return true
  }

  const dismissDiscrepancyModal = () => {
    if (statement?._id) setDiscrepancyDismissedId(statement._id)
  }

  const setUseAi = useAi => {
    setUseAiByField({ tarjeta: useAi, banco: useAi })
  }

  const applyDiscrepancySelection = () => {
    if (!discrepancyGroups.length || !isAiSelected() || !loadedCardMatch) {
      dismissDiscrepancyModal()
      return
    }

    if (acceptAiValue()) dismissDiscrepancyModal()
  }

  const hasAiSelection = discrepancyGroups.length > 0 && isAiSelected() && Boolean(loadedCardMatch)

  const showDiscrepancyModal =
    isOpen &&
    !isMinimized &&
    isReviewing &&
    pendingDiscrepancies.length > 0 &&
    discrepancyDismissedId !== statement?._id

  const confirmMutation = useMutation({
    mutationFn: () => CreditCardStatementApi.confirm(businessId, statementId, {
      bankId: selectedCard.bankId,
      creditCardId: selectedCard.id,
      periodoDesde,
      periodoHasta,
      movimientos: movimientosEdit.map(row => ({
        fecha: row.fecha,
        descripcion: row.descripcion,
        lugarOperacion: row.lugarOperacion || undefined,
        numeroCuota: row.numeroCuota || undefined,
        codigoReferencia: row.codigoReferencia || undefined,
        valorCuotaMensual: Number(row.valorCuotaMensual),
        comentario: row.comentario || undefined
      }))
    }),
    onSuccess: confirmed => {
      queryClient.setQueryData(['credit-card-statement-status', businessId, statementId], confirmed)
      setError(null)
    },
    onError: confirmError => {
      setError(confirmError.response?.data?.message || 'No se pudo confirmar la cartola.')
    }
  })

  const isConfirming = confirmMutation.isPending

  const reviewInitializedIdRef = useRef(null)

  useEffect(() => {
    if (statement?.status !== 'needs_review') return
    if (reviewInitializedIdRef.current === statement._id) return
    reviewInitializedIdRef.current = statement._id
    setResolvedDiscrepancyFields([])
    setUseAiByField({})
    setOpenSimilarIndex(null)
    setMovimientosEdit(
      statement.movimientos.map(movement => ({
        fecha: movement.fecha,
        descripcion: movement.descripcion,
        lugarOperacion: movement.lugarOperacion || '',
        numeroCuota: movement.numeroCuota || '',
        codigoReferencia: movement.codigoReferencia || '',
        valorCuotaMensual: movement.valorCuotaMensual,
        comentario: movement.comentario || '',
        isNew: movement.isNew,
        similarExistente: movement.similarExistente,
        movimientoParecidoFecha: movement.movimientoParecidoFecha,
        movimientoParecidoDescripcion: movement.movimientoParecidoDescripcion,
        movimientoParecidoMonto: movement.movimientoParecidoMonto
      }))
    )
  }, [statement])

  const updateMovimiento = (index, field, value) => {
    setMovimientosEdit(prev => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }

  const removeMovimiento = index => {
    setMovimientosEdit(prev => prev.filter((_, i) => i !== index))
  }

  const addMovimiento = () => {
    setMovimientosEdit(prev => [
      ...prev,
      { fecha: '', descripcion: '', lugarOperacion: '', numeroCuota: '', codigoReferencia: '', valorCuotaMensual: 0, comentario: '' }
    ])
  }

  const notifiedStatementIdRef = useRef(null)

  useEffect(() => {
    if (!statement) return
    const notificationKey = `${statement._id}:${statement.status}`
    if (notificationKey === notifiedStatementIdRef.current) return

    if (statement.status === 'completed' || statement.status === 'needs_review' || statement.status === 'failed') {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ORGANIZATION_TOKENS_AVAILABLE] })
    }

    if (statement.status === 'completed') {
      notifiedStatementIdRef.current = notificationKey
      const movimientosDuplicados = statement.movimientosDuplicados || 0
      sileo.success({
        title: 'Cartola procesada',
        description: (
          <span className='text-green-500/80 font-medium'>
            Se agregaron {statement.movimientosNuevos ?? statement.movimientos.length} movimientos nuevos de tu tarjeta de crédito.
            {movimientosDuplicados > 0 && ` ${movimientosDuplicados} ya existían y no se duplicaron.`}
          </span>
        )
      })
    } else if (statement.status === 'needs_review') {
      notifiedStatementIdRef.current = notificationKey
      sileo.info({
        title: 'Revisa los movimientos extraídos',
        description: (
          <span className='text-amber-600/80 font-medium'>
            TryLuca terminó de leer la cartola. Revisa y edita lo que haga falta antes de generar los asientos.
          </span>
        )
      })
    } else if (statement.status === 'failed') {
      notifiedStatementIdRef.current = notificationKey
      sileo.error({
        title: 'Error al procesar la cartola',
        description: (
          <span className='text-red-500/80 font-medium'>
            {statement.errorMessage || 'No se pudo procesar la cartola.'}
          </span>
        )
      })
    }
  }, [statement, queryClient])

  const resetState = () => {
    setSelectedFile(null)
    setSelectedCard(null)
    setPeriodoMes(getCurrentPeriodoMes())
    setError(null)
    setStatementId(null)
    setIsMinimized(false)
    setMovimientosEdit([])
    setResolvedDiscrepancyFields([])
    setDiscrepancyDismissedId(null)
    setUseAiByField({})
    setShowCreateCardModal(false)
    setOpenSimilarIndex(null)
    estimateTokensMutation.reset()
    reviewInitializedIdRef.current = null
    notifiedStatementIdRef.current = null
  }

  const handleClose = () => {
    if (statement?.status === 'completed') {
      queryClient.removeQueries({ queryKey: [QueryKeys.BANK_TRANSACTIONS], exact: false })
      queryClient.invalidateQueries({ queryKey: ['credit-cards', businessId] })
    }
    resetState()
    onClose()
  }

  const handleMinimize = () => {
    setIsMinimized(true)
  }

  const handleExpand = () => {
    setIsMinimized(false)
  }

  const handleCardCreated = created => {
    queryClient.setQueryData(['credit-cards', businessId], prev => {
      const list = prev || []
      if (list.some(card => card.id === created.id)) return list
      return [...list, created]
    })
    queryClient.invalidateQueries({ queryKey: ['credit-cards', businessId] })
    setSelectedCard(created)
    setShowCreateCardModal(false)
    setError(null)
    const extractedLast4 = last4FromValue(statement?.numeroTarjetaExtraido)
    if (extractedLast4 && normalizeText(created.last4Digits) === normalizeText(extractedLast4)) {
      setUseAiByField(prev => ({ ...prev, tarjeta: true, banco: true }))
    }
  }

  const handleFileChange = event => {
    const file = event.target.files?.[0] || null
    setSelectedFile(file)
    setStatementId(null)
    setError(null)
    event.target.value = ''

    if (file) {
      estimateTokensMutation.mutate(file)
    } else {
      estimateTokensMutation.reset()
    }
  }

  const handleSubmit = () => {
    if (!businessId) {
      setError('No hay una empresa seleccionada.')
      return
    }
    if (!selectedCard) {
      setError('falta completar el campo: tarjeta')
      return
    }
    if (!selectedFile) {
      setError('falta completar el campo: archivo')
      return
    }

    setError(null)
    saveMutation.mutate()
  }

  const currencyMismatchMessage = statement?.monedaExtraida
    ? `La cartola es de una tarjeta en ${statement.monedaExtraida}, no corresponde a la tarjeta seleccionada. Debes seleccionar o crear la tarjeta como ${statement.monedaExtraida}.`
    : ''

  const handleConfirm = () => {
    if (confirmMutation.isPending) return
    if (!selectedCard) {
      setError('falta completar el campo: tarjeta')
      return
    }
    if (!movimientosEdit.length) {
      setError('falta completar el campo: movimientos')
      return
    }
    if (!cardCurrencyMatchesStatement(selectedCard, statement)) {
      setError(currencyMismatchMessage)
      return
    }

    setError(null)
    confirmMutation.mutate()
  }

  const fieldsDisabled = isProcessing || isConfirming || isFinished
  const canMinimize = isProcessing || isConfirming || isReviewing
  const availableTokensLabel = tokensInfoQuery.data?.available?.toLocaleString() ?? '—'
  const hasActualTokenUsage = (isReviewing || isFinished) && typeof statement?.tokensUsed === 'number'
  const tokenUsageMessage = !selectedFile
    ? null
    : hasActualTokenUsage
      ? `Esto gastó ${statement.tokensUsed.toLocaleString()} tokens de tu plan actual. Tienes ${availableTokensLabel} tokens disponibles.`
      : estimateTokensMutation.isPending
        ? 'Calculando el costo estimado en tokens...'
        : estimateTokensMutation.isError
          ? 'No pudimos estimar el costo en tokens de este archivo.'
          : estimateTokensMutation.data
            ? `Esto consumirá aproximadamente ${estimateTokensMutation.data.estimatedTokens.toLocaleString()} tokens de tu plan actual. Tienes ${availableTokensLabel} tokens disponibles.`
            : null

  return (
    <>
      <LucaModal
        open={isOpen && !isMinimized}
        onClose={canMinimize ? handleMinimize : handleClose}
        onMinimize={canMinimize ? handleMinimize : null}
        maxWidth='max-w-5xl'
        footer={
          isProcessing || isConfirming ? (
            <div className='flex w-full items-center justify-between gap-4'>
              <div className='flex items-center gap-2 text-primary font-bold'>
                <span className='relative flex h-3 w-3'>
                  <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75' />
                  <span className='relative inline-flex rounded-full h-3 w-3 bg-primary' />
                </span>
                <span className='text-xs'>
                  {saveMutation.isPending
                    ? 'Subiendo cartola...'
                    : isConfirming
                      ? 'Agregando movimientos de tu tarjeta de credito...'
                      : 'Extrayendo movimientos en segundo plano...'}
                </span>
              </div>
              <Button
                title='Minimizar'
                onClick={handleMinimize}
                className='text-[10px] bg-primary text-white rounded-full'
              />
            </div>
          ) : isReviewing ? (
            <>
              <Button
                title='Cerrar'
                onClick={handleClose}
                className='text-[10px] bg-primary text-white rounded-full'
              />
              <Button
                title='Minimizar'
                onClick={handleMinimize}
                className='text-[10px] bg-primary text-white rounded-full'
              />
              <Button
                title='Aceptar y guardar la información'
                onClick={handleConfirm}
                disabled={isConfirming}
                className='text-[10px] bg-accent2 text-white rounded-full'
              />
            </>
          ) : isFinished ? (
            <div className='flex w-full items-center justify-end'>
              <Button
                title='Cerrar'
                onClick={handleClose}
                className='text-[10px] bg-primary text-white rounded-full'
              />
            </div>
          ) : (
            <>
              <Button
                title='Cerrar'
                onClick={handleClose}
                className='text-[10px] bg-primary text-white rounded-full'
              />
              <Button
                title='Subir y guardar'
                onClick={handleSubmit}
                className='text-[10px] bg-accent2 text-white rounded-full'
              />
            </>
          )
        }
      >
        <div className='flex-1 overflow-y-auto p-6'>
          <div className='flex items-center gap-3'>
            <span className='relative flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-primary shadow-lg ring-2 ring-accent2/20'>
              <img src={robot} alt='Luca AI' draggable={false} className='h-7 w-7 object-contain' />
              <span className='absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-accent2 ring-2 ring-white'>
                <Sparkles size={9} className='text-white' />
              </span>
            </span>
            <div>
              <h3 className='text-lg font-bold text-slate-900'>Agregar tarjeta de crédito</h3>
              <div className='inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-accent2'>
                <Sparkles size={11} />
                Extracción con TryLuca
              </div>
            </div>
          </div>
          <p className='mt-3 text-xs text-slate-500'>
            Selecciona la tarjeta (o agrega una nueva) y sube la cartola.
            Los movimientos se extraen automáticamente y quedan disponibles para revisión antes de generar los asientos.
          </p>

          <div className='mt-5'>
            <label className='block text-xs font-semibold text-slate-600 mb-1 leading-4'>Tarjeta</label>
            <CreditCardSelect
              businessId={businessId}
              value={selectedCard}
              onChange={setSelectedCard}
              disabled={fieldsDisabled}
            />
          </div>

          <div className='mt-5'>
            <label className='block text-xs font-semibold text-slate-600 mb-1'>Cartola</label>
            <div className='flex items-center justify-center gap-4 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 p-5'>
              <div className='h-10 w-10 rounded-full bg-white text-slate-400 shadow-sm flex items-center justify-center flex-shrink-0'>
                <CloudUpload size={22} />
              </div>

              <div className='flex-1'>
                <input
                  id='credit-card-statement-file'
                  type='file'
                  accept='.pdf,.xlsx,.docx,.txt,.csv'
                  onChange={handleFileChange}
                  disabled={isProcessing}
                  className='sr-only'
                />
                <label
                  htmlFor='credit-card-statement-file'
                  className={`inline-flex items-center justify-center gap-2 rounded-full bg-accent2 px-5 py-2 text-xs font-bold text-white shadow transition-all hover:bg-accent2/90 active:scale-95 ${
                    isProcessing ? 'pointer-events-none opacity-60' : 'cursor-pointer'
                  }`}
                >
                  Seleccionar archivo
                </label>
              </div>

              <div
                className={`rounded-full border px-3 py-1.5 text-xs whitespace-nowrap ${selectedFile
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-slate-200 bg-white text-slate-400'
                }`}
              >
                {selectedFile ? selectedFile.name : 'No seleccionado'}
              </div>
            </div>

            {tokenUsageMessage && (
              <p className='mt-2 text-[11px] text-slate-500'>
                {tokenUsageMessage}
              </p>
            )}
          </div>

          {(isProcessing || isConfirming) && (
            <div className='mt-4 flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3'>
              <span className='relative flex h-9 w-9 flex-shrink-0 items-center justify-center'>
                <span className='absolute inset-0 rounded-full bg-primary/20 animate-ping' />
                <span className='relative flex h-9 w-9 items-center justify-center rounded-full bg-primary shadow-md'>
                  <img src={robot} alt='Luca AI' draggable={false} className='h-5 w-5 object-contain' />
                </span>
              </span>
              <p className='text-xs font-medium text-primary'>
                {saveMutation.isPending
                  ? 'Subiendo el archivo...'
                  : isConfirming
                    ? 'Agregando los movimientos de tu tarjeta de credito...'
                    : 'TryLuca está leyendo la cartola y extrayendo los movimientos. Puedes minimizar esta ventana y seguir trabajando.'}
              </p>
            </div>
          )}

          {error && (
            <div className='mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'>
              {error}
            </div>
          )}

          {statement?.status === 'failed' && (
            <div className='mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'>
              {statement.errorMessage || 'No se pudo procesar la cartola.'}
            </div>
          )}

          {statusQuery.isError && !statement && (
            <div className='mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'>
              No pudimos consultar el estado de la cartola. Intenta cerrar y volver a subirla.
            </div>
          )}

          {isReviewing && (
            <div className='mt-6'>
              <div className='overflow-x-auto rounded-xl border border-slate-200'>
                <p className='px-4 pt-3 text-xs text-slate-500'>
                  N° tarjeta detectado: {statement.numeroTarjetaExtraido || 'no detectado'} · {movimientosEdit.length}{' '}
                  movimientos · edita lo que haga falta antes de aceptar
                  {movimientosEdit.some(row => row.isNew === false) &&
                    ` · ${movimientosEdit.filter(row => row.isNew === false).length} ya existían`}
                </p>
                {(selectedCard?.currency || statement?.monedaExtraida) === 'USD' && (
                  <p className='px-4 pt-1 text-xs text-slate-500'>
                    Los movimientos se guardarán en CLP con el valor del dólar del día de cada transacción.
                  </p>
                )}
                <table className='min-w-full border-collapse text-sm'>
                  <thead>
                    <tr className='border-b border-slate-200 text-left text-slate-500'>
                      {EDITABLE_MOVEMENT_FIELDS.map(field => (
                        <Fragment key={field.key}>
                          <th className='px-2 py-2 font-medium'>{field.label}</th>
                          {field.key === 'valorCuotaMensual' && (
                            <th className='px-2 py-2 font-medium'>Moneda</th>
                          )}
                        </Fragment>
                      ))}
                      <th className='px-2 py-2 font-medium'>Estado</th>
                      <th className='px-2 py-2 font-medium' />
                    </tr>
                  </thead>
                  <tbody>
                    {movimientosEdit.map((row, index) => (
                      <tr key={index} className='border-b border-slate-100'>
                        {EDITABLE_MOVEMENT_FIELDS.map(field => (
                          <Fragment key={field.key}>
                            <td className='px-1.5 py-1.5'>
                              <input
                                type={field.type}
                                value={row[field.key] ?? ''}
                                placeholder={field.placeholder}
                                onChange={event => updateMovimiento(index, field.key, event.target.value)}
                                className='w-full border border-gray-200 px-2 py-1 rounded-lg text-xs font-sans focus:outline-none focus:ring-1 focus:ring-primary'
                              />
                            </td>
                            {field.key === 'valorCuotaMensual' && (
                              <td className='px-1.5 py-1.5'>
                                <span className='inline-block min-w-[3.5rem] rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500'>
                                  {selectedCard?.currency || statement?.monedaExtraida || 'CLP'}
                                </span>
                              </td>
                            )}
                          </Fragment>
                        ))}
                        <td className='px-1.5 py-1.5'>
                          <MovementStatusBadge
                            isNew={row.isNew}
                            similarExistente={row.similarExistente}
                            movimientoParecidoFecha={row.movimientoParecidoFecha}
                            movimientoParecidoDescripcion={row.movimientoParecidoDescripcion}
                            movimientoParecidoMonto={row.movimientoParecidoMonto}
                            isSimilarInfoOpen={openSimilarIndex === index}
                            onToggleSimilarInfo={() =>
                              setOpenSimilarIndex(prev => (prev === index ? null : index))
                            }
                          />
                        </td>
                        <td className='px-1.5 py-1.5 text-center'>
                          <button
                            type='button'
                            onClick={() => removeMovimiento(index)}
                            title='Eliminar movimiento'
                            className='text-rose-500 hover:text-rose-700'
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className='p-3'>
                  <button
                    type='button'
                    onClick={addMovimiento}
                    className='text-xs font-semibold text-primary hover:underline'
                  >
                    + Agregar movimiento
                  </button>
                </div>
              </div>
            </div>
          )}

          {statement?.status === 'completed' && (
            <div className='mt-6 overflow-x-auto rounded-xl border border-slate-200'>
              <p className='px-4 pt-3 text-xs text-slate-500'>
                N° tarjeta detectado: {statement.numeroTarjetaExtraido || 'no detectado'} · {statement.movimientos.length} movimientos
                {' '}({statement.movimientosNuevos ?? statement.movimientos.length} nuevos
                {statement.movimientosDuplicados > 0 ? `, ${statement.movimientosDuplicados} ya existían` : ''})
              </p>
              {(selectedCard?.currency || statement?.monedaExtraida) === 'USD' && (
                <p className='px-4 pt-1 text-xs text-slate-500'>
                  Los movimientos se guardarán en CLP con el valor del dólar del día de cada transacción.
                </p>
              )}
              <table className='min-w-full border-collapse text-sm'>
                <thead>
                  <tr className='border-b border-slate-200 text-left text-slate-500'>
                    {MOVEMENT_COLUMNS.map(column => (
                      <Fragment key={column.key}>
                        <th className='px-4 py-2 font-medium'>{column.label}</th>
                        {column.key === 'cargo' && (
                          <th className='px-4 py-2 font-medium'>Moneda</th>
                        )}
                      </Fragment>
                    ))}
                    <th className='px-4 py-2 font-medium'>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {statement.movimientos.map((movement, index) => (
                    <tr key={index} className='border-b border-slate-100'>
                      {MOVEMENT_COLUMNS.map(column => (
                        <Fragment key={column.key}>
                          <td className='px-4 py-2 text-slate-800'>
                            {movement[column.key] ?? ''}
                          </td>
                          {column.key === 'cargo' && (
                            <td className='px-4 py-2'>
                              <span className='inline-block rounded-lg border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500'>
                                {selectedCard?.currency || statement?.monedaExtraida || 'CLP'}
                              </span>
                            </td>
                          )}
                        </Fragment>
                      ))}
                      <td className='px-4 py-2'>
                        <MovementStatusBadge
                          isNew={movement.isNew}
                          similarExistente={movement.similarExistente}
                          movimientoParecidoFecha={movement.movimientoParecidoFecha}
                          movimientoParecidoDescripcion={movement.movimientoParecidoDescripcion}
                          movimientoParecidoMonto={movement.movimientoParecidoMonto}
                          isSimilarInfoOpen={openSimilarIndex === index}
                          onToggleSimilarInfo={() =>
                            setOpenSimilarIndex(prev => (prev === index ? null : index))
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </LucaModal>

      <LucaModal
        open={showDiscrepancyModal}
        onClose={dismissDiscrepancyModal}
        maxWidth='max-w-3xl'
        zIndex='z-[1400]'
        footer={
          <>
            <Button
              title='Mantener mi selección'
              onClick={dismissDiscrepancyModal}
              className='text-[10px] bg-primary text-white rounded-full'
            />
            <Button
              title='Aplicar'
              onClick={applyDiscrepancySelection}
              disabled={!hasAiSelection}
              className='text-[10px] bg-accent2 text-white rounded-full'
            />
          </>
        }
      >
        <div className='flex-1 overflow-y-auto p-6'>
          <h3 className='text-lg font-bold text-slate-900'>La cartola no coincide con lo que elegiste</h3>
          <p className='mt-2 text-sm text-slate-500'>
            El PDF tiene datos distintos a la tarjeta que seleccionaste. Por defecto se usa lo que detectó TryLuca.
          </p>

          <div className='mt-5 space-y-4'>
            {discrepancyGroups.map(group => {
              const canUseAi = Boolean(loadedCardMatch)
              const useAi = canUseAi && isAiSelected()
              const selectedLabel = selectedCard ? maskCardLabel(selectedCard) : group.items[0].selectedValue
              const aiLabel = loadedCardMatch ? maskCardLabel(loadedCardMatch) : extractedCardLabel(statement, group.items)

              return (
                <div
                  key={group.key}
                  className='rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'
                >
                  <p className='text-xs font-bold uppercase tracking-wide text-primary'>
                    {DISCREPANCY_LABELS[group.field] || group.field}
                  </p>
                  <div className='mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2'>
                    <label
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 transition-colors ${
                        !useAi ? 'border-primary bg-primary/5' : 'border-slate-200 bg-slate-50'
                      }`}
                    >
                      <input
                        type='checkbox'
                        checked={!useAi}
                        onChange={() => setUseAi(false)}
                        className='mt-0.5 h-4 w-4 shrink-0 rounded-sm border-gray-300 text-primary accent-primary'
                      />
                      <span>
                        <span className='block text-[11px] font-medium text-slate-400'>Lo que elegiste</span>
                        <span className='block text-sm font-semibold text-slate-900'>{selectedLabel}</span>
                      </span>
                    </label>
                    {canUseAi ? (
                      <label
                        className={`flex cursor-pointer items-start gap-3 rounded-xl border px-3 py-3 transition-colors ${
                          useAi ? 'border-primary bg-primary/5' : 'border-slate-200 bg-slate-50'
                        }`}
                      >
                        <input
                          type='checkbox'
                          checked={useAi}
                          onChange={() => setUseAi(true)}
                          className='mt-0.5 h-4 w-4 shrink-0 rounded-sm border-gray-300 text-primary accent-primary'
                        />
                        <span>
                          <span className='block text-[11px] font-medium text-accent2'>Lo que dice TryLuca</span>
                          <span className='block text-sm font-semibold text-slate-900'>{aiLabel}</span>
                        </span>
                      </label>
                    ) : (
                      <div className='rounded-xl border border-slate-200 bg-slate-50 px-3 py-3'>
                        <span className='block text-[11px] font-medium text-accent2'>Lo que dice TryLuca</span>
                        <span className='block text-sm font-semibold text-slate-900'>{aiLabel}</span>
                        <p className='mt-2 text-xs font-semibold text-slate-700'>
                          {cardCurrencyMatchesStatement(selectedCard, statement)
                            ? 'Debes crear primero esta tarjeta.'
                            : currencyMismatchMessage}
                        </p>
                        <button
                          type='button'
                          onClick={() => setShowCreateCardModal(true)}
                          className='mt-2 inline-flex items-center gap-1 text-xs font-bold text-accent2 hover:underline'
                        >
                          <Plus size={12} />
                          Crear tarjeta
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {error && (
            <div className='mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'>
              {error}
            </div>
          )}
        </div>
      </LucaModal>

      <ModalCreateCreditCard
        isOpen={showCreateCardModal}
        onClose={() => setShowCreateCardModal(false)}
        businessId={businessId}
        onCreated={handleCardCreated}
        zIndex='z-[1500]'
        initialBankId={selectedCard?.bankId ?? ''}
        initialBankName={statement?.bancoExtraido || selectedCard?.bankName || ''}
        initialLast4Digits={selectedCard?.last4Digits ?? ''}
        initialCurrencyId={CURRENCY_ID_BY_CODE[statement?.monedaExtraida] ?? CLP_CURRENCY_ID}
      />

      {isOpen && isMinimized && (
        <div className='fixed bottom-6 right-6 z-[1300] w-80 rounded-2xl bg-white shadow-2xl overflow-hidden border border-slate-200'>
          <div className='w-full bg-primary px-4 py-2 flex items-center justify-between gap-2'>
            <div className='flex items-center gap-2 text-white'>
              <CreditCard size={16} />
              <span className='text-xs font-bold'>Tarjeta de crédito</span>
            </div>
            <div className='flex items-center gap-1'>
              <button
                type='button'
                onClick={handleExpand}
                title='Expandir'
                className='text-white hover:bg-white/20 rounded p-0.5 transition-colors'
              >
                <Maximize2 size={16} />
              </button>
              <button
                type='button'
                onClick={handleClose}
                title='Cerrar'
                className='text-white hover:bg-white/20 rounded p-0.5 transition-colors'
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className='p-4 space-y-2'>
            <p className='text-sm font-semibold text-slate-800'>
              {selectedCard ? maskCardLabel(selectedCard) : '—'}
            </p>

            {(isProcessing || isConfirming) && (
              <div className='flex items-center gap-2 text-primary'>
                <span className='relative flex h-2.5 w-2.5'>
                  <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75' />
                  <span className='relative inline-flex rounded-full h-2.5 w-2.5 bg-primary' />
                </span>
                <span className='text-xs font-semibold'>
                  {saveMutation.isPending
                    ? 'Subiendo cartola...'
                    : isConfirming
                      ? 'Agregando movimientos de tu tarjeta de credito...'
                      : 'Extrayendo movimientos...'}
                </span>
              </div>
            )}

            {!isProcessing && !isConfirming && (error || statement?.status === 'failed') && (
              <p className='text-xs font-medium text-rose-600'>
                {error || statement?.errorMessage || 'No se pudo procesar la cartola.'}
              </p>
            )}

            {!isProcessing && !isConfirming && statement?.status === 'completed' && (
              <p className='text-xs font-medium text-emerald-600'>
                Cartola procesada · {statement.movimientosNuevos ?? statement.movimientos.length} nuevos
              </p>
            )}

            {!isProcessing && !isConfirming && statement?.status === 'needs_review' && (
              <p className='text-xs font-medium text-amber-600'>Revisa los movimientos antes de aceptar</p>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default ModalCreditCardStatement
